import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import { adminSettingsApi } from "@profitlens/api-client";
import type { Plan, PlanConfigItem } from "@profitlens/api-client";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";

interface EditablePlan extends PlanConfigItem {
  /** feature_flags 的文本编辑态 */
  flagsText: string;
}

export function PlansPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["admin", "settings", "plans"],
    queryFn: () => adminSettingsApi.getPlans(),
  });

  const [trialDays, setTrialDays] = useState("14");
  const [rows, setRows] = useState<EditablePlan[]>([]);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setTrialDays(String(s.trial_days));
    setRows(
      s.items.map((item) => ({
        code: item.code,
        plan: item.plan,
        days: item.days,
        amount: item.amount,
        currency: item.currency,
        feature_flags: item.feature_flags ?? null,
        flagsText: item.feature_flags ? JSON.stringify(item.feature_flags) : "",
        enabled: item.enabled,
        sort_order: item.sort_order,
      })),
    );
  }, [settings.data]);

  const updateRow = (index: number, patch: Partial<EditablePlan>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const save = useMutation({
    mutationFn: () => {
      const items: PlanConfigItem[] = rows.map((row) => {
        let flags: Record<string, unknown> | null = null;
        if (row.flagsText.trim()) {
          const parsed: unknown = JSON.parse(row.flagsText);
          if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
            throw new Error(t("settings.planFlagsInvalid"));
          }
          flags = parsed as Record<string, unknown>;
        }
        return {
          code: row.code,
          plan: row.plan,
          days: row.days,
          amount: row.amount,
          currency: row.currency.toUpperCase(),
          feature_flags: flags,
          enabled: row.enabled,
          sort_order: row.sort_order,
        };
      });
      return adminSettingsApi.putPlans({
        trial_days: Math.max(0, Number.parseInt(trialDays, 10) || 0),
        items,
      });
    },
    onSuccess: async () => {
      setFeedback({ kind: "ok", text: t("settings.plansSaved") });
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings", "plans"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] });
    },
    onError: (err) =>
      setFeedback({
        kind: "err",
        text:
          err instanceof Error && err.message === t("settings.planFlagsInvalid")
            ? err.message
            : translateError(err, t),
      }),
  });

  if (settings.isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("settings.plansTitle")}
        subtitle={t("settings.plansSubtitle")}
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-muted">
              {t("settings.trialDays")}
              <Input
                type="number"
                min={0}
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="w-24"
              />
            </label>
            <Button
              variant="outline"
              onClick={() =>
                setRows((prev) => [
                  ...prev,
                  {
                    code: "",
                    plan: "pro",
                    days: 30,
                    amount: "15.00",
                    currency: "USD",
                    feature_flags: null,
                    flagsText: "",
                    enabled: true,
                    sort_order: prev.length,
                  },
                ])
              }
            >
              <Plus className="h-4 w-4" />
              {t("settings.addPlan")}
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || rows.length === 0}>
              {save.isPending ? t("common.saving") : t("settings.plansSave")}
            </Button>
          </div>
        }
      />

      {feedback ? (
        <p className={feedback.kind === "ok" ? "text-sm text-success" : "text-sm text-danger"}>
          {feedback.text}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("settings.plansEmpty")}</Card>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("settings.planCode")}</TableHead>
                <TableHead>{t("settings.planTier")}</TableHead>
                <TableHead>{t("settings.planDays")}</TableHead>
                <TableHead>{t("settings.planAmount")}</TableHead>
                <TableHead>{t("common.currency")}</TableHead>
                <TableHead className="min-w-56">{t("settings.planFlags")}</TableHead>
                <TableHead>{t("settings.planSort")}</TableHead>
                <TableHead>{t("settings.planEnabled")}</TableHead>
                <TableHead className="w-16">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      value={row.code}
                      onChange={(e) => updateRow(index, { code: e.target.value })}
                      placeholder="pro_monthly"
                      title={t("settings.planCodeHint")}
                      className="w-36 font-mono text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.plan}
                      onValueChange={(v) => updateRow(index, { plan: v as Plan })}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">{t("tenants.planFree")}</SelectItem>
                        <SelectItem value="pro">{t("tenants.planPro")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={1}
                      value={row.days}
                      onChange={(e) =>
                        updateRow(index, { days: Number.parseInt(e.target.value, 10) || 30 })
                      }
                      className="w-20"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.amount}
                      onChange={(e) => updateRow(index, { amount: e.target.value })}
                      className="w-24 tabular-nums"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.currency}
                      onChange={(e) => updateRow(index, { currency: e.target.value })}
                      maxLength={3}
                      className="w-20 uppercase"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.flagsText}
                      onChange={(e) => updateRow(index, { flagsText: e.target.value })}
                      placeholder={t("settings.planFlagsHint")}
                      className="font-mono text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={row.sort_order}
                      onChange={(e) =>
                        updateRow(index, { sort_order: Number.parseInt(e.target.value, 10) || 0 })
                      }
                      className="w-16"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={row.enabled}
                      onCheckedChange={(v) => updateRow(index, { enabled: v })}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t("settings.removePlan")}
                      onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
